
import device from 'bits/device'
import type { ResizeObserver } from 'bits/global'
import { observeSizeChange } from 'bits/resize_observer'
import { Component, Prop, Vue } from 'bits/vue'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzOverlay from 'library/v4/components/OzOverlay.vue'
import OzPopoverTip from 'library/v4/components/OzPopoverTip.vue'
import { debounce } from 'lodash-es'
import type { VueCssClass } from 'types'

export interface PopoverAnchor {
  position?: 'top' | 'side' | 'bottom'
  top: number
  left: number
  width: number
  height: number
}
export interface PopoverManualPosition {
  tipSide?: 'left' | 'right'
  boxTopOffset?: number
  boxLeftOffset?: number
}

export function getAnchorFromCoordinates(
  x: number,
  y: number,
  offset: { top: number; left: number } = { top: 0, left: 0 },
): Partial<PopoverAnchor> {
  return {
    top: y + offset.top,
    left: x + offset.left,
    width: 0,
    height: 0,
  }
}
export function getAnchorFromElement(
  el: Element,
  offset: { top: number; left: number } = { top: 0, left: 0 },
): Partial<PopoverAnchor> {
  const rect = el.getBoundingClientRect()
  return {
    top: rect.top + offset.top,
    left: rect.left + offset.left,
    width: rect.width,
    height: rect.height,
  }
}

@Component({ components: { OzBox, OzOverlay, OzPopoverTip } })
export default class OzPopoverModal extends Vue {
  $refs!: {
    popover: OzBox
  }

  /**
   * Show or hide header box
   */
  @Prop({ default: true }) xHeader!: boolean
  /**
   * Show or hide scrim
   */
  @Prop({ default: true }) xScrim!: boolean
  /**
   * See OzOverlay#isSpectral
   */
  @Prop({ default: false }) isSpectral!: boolean
  /**
   * Height of the container containing the popover and its scrim background, default to window height
   */
  @Prop({ default: undefined }) containerHeight!: number
  /**
   * Width of the container containing the popover and its scrim background, default to window width
   */
  @Prop({ default: undefined }) containerWidth!: number
  /**
   * Width of the popover, default to 224px. Wide is 320px. Auto will calculate the min width required on mount.
   */
  @Prop({ default: 'default' }) popoverWidth!: 'default' | 'wide' | number | 'auto'
  /**
   * Compulsory anchor location of the popover to determine where the popover is rendered.
   *
   * All numbers are with regards to the container containing the popover.
   *
   * Anchor could be a button or an icon.
   *
   * You can use `getAnchorFromElement` function provided in this component.
   *
   * Properties are:
   *
   * - `position`: 'top'|'side'|'bottom'
   * - `top`: number
   * - `left`: number
   * - `width`: number
   * - `height`: number
   */
  @Prop({ required: true }) popoverAnchor!: PopoverAnchor
  /**
   * Color scheme.
   *
   *
   * **Primary** - base page background color
   *
   * - Light mode: white
   * - Dark mode: black
   *
   *
   * **Secondary** - use for section boxes over Primary background
   *
   * - Light mode: light grey
   * - Dark mode: dark grey
   *
   *
   * **Selection** - color of active/selected items
   *
   * - Light mode: grape
   * - Dark mode: canary
   *
   *
   * **Highlight** - color of a highlighted section
   *
   * - Light mode: light canary
   * - Dark mode: nightfall
   *
   *
   * **Destructive** - color of a highlighted section
   *
   * - Light mode: tangerine
   * - Dark mode: nightfall
   *
   *
   * **Pink** - color of a highlighted section
   *
   * - Light mode: padlet pink
   * - Dark mode: nightfall
   *
   *
   * **null** - transparent
   *
   */
  @Prop({ default: OzBoxColors.Primary }) color!: OzBoxColors
  /**
   * See OzBox#radius
   */
  @Prop({ default: 20 }) radius!: 0 | 8 | 12 | 16 | 20 | 24
  /**
   * Extra classes for the box, for example to set a maximum height or add box shadow
   */
  @Prop({ default: () => [] }) boxClasses!: VueCssClass
  /**
   * Extra classes for the tip, for example to set a custom color
   */
  @Prop({ default: () => [] }) tipClasses!: VueCssClass
  /**
   * Extra classes for the tip svg path, for example to add drop shadow
   */
  @Prop({ default: () => [] }) tipPathClasses!: VueCssClass
  /**
   * Dark mode for color scheme
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'
  /**
   * Direction of the popover, either ltr or rtl
   */
  @Prop({
    default: () => document?.documentElement?.dir || 'ltr',
  })
  dir!: 'ltr' | 'rtl'

  /**
   * Smart position mode will render the popover within the container/viewport.
   *
   * Manual position mode will render the popover according to manualPosition prop
   */
  @Prop({ default: 'smart' }) positionMode!: 'smart' | 'manual'
  /**
   * Ignored in smart position mode. Properties are:
   *
   * - `tipSide`: `'left' | 'right'` -- valid for side popover
   *
   * - `boxTopOffset`: `number` -- valid for side popover
   *
   * - `boxLeftOffset`: `number` -- valid for top popover
   */
  @Prop({ default: () => ({}) }) manualPosition!: PopoverManualPosition

  /**
   * Optional, if you are using blur color scheme, this is needed for firefox to display correctly.
   */
  @Prop({ default: () => !!device?.firefox }) isFirefox!: boolean
  /**
   * Whether there is a popover tip, as that determines positioning of the popover modal
   */
  @Prop({ default: true }) xPopoverTip!: boolean

  OzBoxColors = OzBoxColors
  GAP_TO_ANCHOR = 2
  MIN_SCREEN_EDGE_PADDING = 12
  SIDE_POPOVER_TIP_DISTANCE_FROM_TOP = 20
  POPOVER_TIP_HEIGHT_SIDE = 24
  POPOVER_TIP_WIDTH_SIDE = 12
  POPOVER_TIP_HEIGHT_VERTICAL = 12
  POPOVER_TIP_WIDTH_VERTICAL = 24
  TOP_EDGE_MARGIN = 32

  get anchorDistanceToBottom(): number {
    return this.realContainerHeight - this.popoverAnchor.top
  }

  get isRtl(): boolean {
    return this.dir === 'rtl'
  }

  autoCalculatedPopoverWidthPx = 0

  get popoverWidthPx(): number {
    if (typeof this.popoverWidth === 'number') {
      return this.popoverWidth
    }

    if (this.popoverWidth === 'auto') {
      return this.autoCalculatedPopoverWidthPx
    }

    // value must match tailwindcss class values
    switch (this.popoverWidth) {
      case 'wide':
        // w-80
        return 320
      case 'default':
        // w-56
        return 224
      default:
        return 224
    }
  }

  popoverHeightPx = 0
  get willExceedViewportLeftEdge(): boolean {
    if (this.positionMode !== 'smart') return false
    return this.popoverAnchor.left - this.popoverWidthPx < 8
  }

  get popoverPosition(): string {
    return this.popoverAnchor.position || 'top'
  }

  get willExceedViewportRightEdge(): boolean {
    // currently popover modal is only used inside the post modal where it appears above a button and its left position is aligned with the button
    // => popover modal can only go out of bound to the right side of the viewport
    if (this.positionMode !== 'smart') return false
    if (this.popoverPosition === 'top' || this.popoverPosition === 'bottom') {
      return this.popoverAnchor.left + this.popoverWidthPx + 8 > this.realContainerWidth
    }
    return this.popoverAnchor.left + this.popoverAnchor.width + this.popoverWidthPx + 32 > this.realContainerWidth
  }

  get willExceedViewportBottomEdge(): boolean {
    if (this.positionMode !== 'smart') return false
    if (this.popoverPosition === 'side') {
      return this.popoverAnchor.top + this.popoverHeightPx + this.MIN_SCREEN_EDGE_PADDING > this.realContainerHeight
    }
    return false
  }

  get willExceedViewportTopEdge(): boolean {
    return this.popoverAnchor.top - this.popoverHeightPx - this.TOP_EDGE_MARGIN - this.POPOVER_TIP_HEIGHT_VERTICAL < 0
  }

  get isAnchorTooHigh(): boolean {
    if (this.positionMode !== 'smart') return false
    if (this.isAnchorTooLow) {
      return false
    }
    if (this.popoverPosition === 'top') {
      return this.willExceedViewportTopEdge
    }
    return false
  }

  get isAnchorTooLow(): boolean {
    if (this.positionMode !== 'smart') return false
    if (this.popoverPosition === 'side') {
      return this.realContainerHeight - this.popoverAnchor.top - this.popoverAnchor.height < 24
    }
    if (this.popoverPosition === 'bottom') {
      return this.realContainerHeight - this.popoverAnchor.top - this.popoverAnchor.height - this.popoverHeightPx < 24
    }
    return false
  }

  get popoverSidePosition(): 'left' | 'right' {
    if (this.positionMode === 'manual' && this.manualPosition?.tipSide) {
      return this.manualPosition.tipSide
    }
    if (this.popoverPosition === 'bottom') {
      return !this.isRtl
        ? this.willExceedViewportRightEdge
          ? 'left'
          : 'right'
        : this.willExceedViewportLeftEdge
        ? 'right'
        : 'left'
    }
    if ((!this.isRtl && this.willExceedViewportRightEdge) || (this.isRtl && !this.willExceedViewportLeftEdge))
      return 'left'
    return 'right'
  }

  get popoverOnTopStyle(): Record<string, string> {
    if (this.isAnchorTooHigh) {
      return this.popoverToSideStyle
    }
    const popoverLeft =
      this.popoverSidePosition === 'left'
        ? this.popoverAnchor.left + this.popoverAnchor.width / 2 - this.popoverWidthPx + 48
        : this.popoverAnchor.left + this.popoverAnchor.width / 2 - 48
    const manualLeftOffset = this.positionMode === 'manual' ? this.manualPosition?.boxLeftOffset || 0 : 0
    const styles = {
      // leave 12px for the tip + some space between the modal and the anchor
      bottom: `${this.anchorDistanceToBottom + this.POPOVER_TIP_HEIGHT_VERTICAL}px`,
      left: `${popoverLeft + manualLeftOffset}px`,
      width: this.popoverWidth === 'auto' ? 'auto' : `${this.popoverWidthPx}px`,
    }
    return this.positionMode === 'manual' && this.willExceedViewportTopEdge
      ? { ...styles, top: `${this.TOP_EDGE_MARGIN}px` }
      : styles
  }

  get popoverToBottomStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopStyle
    }
    const popoverLeft =
      this.popoverSidePosition === 'right'
        ? this.popoverAnchor.left
        : this.popoverAnchor.left - this.popoverWidthPx + this.popoverAnchor.width
    const popoverTop =
      this.popoverAnchor.top +
      this.popoverAnchor.height +
      (this.xPopoverTip ? this.POPOVER_TIP_HEIGHT_VERTICAL : this.GAP_TO_ANCHOR)
    const manualLeftOffset = this.positionMode === 'manual' ? this.manualPosition?.boxLeftOffset || 0 : 0
    const manualTopOffset = this.positionMode === 'manual' ? this.manualPosition?.boxTopOffset || 0 : 0
    return {
      // leave 12px for the tip + some space between the modal and the anchor
      top: `${popoverTop + manualTopOffset}px`,
      left: `${popoverLeft + manualLeftOffset}px`,
      width: this.popoverWidth === 'auto' ? 'auto' : `${this.popoverWidthPx}px`,
    }
  }

  get popoverToSideStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopStyle
    }

    // When there is no popover, we want the popover to be closer to click position, in ltr and rtl languages.
    const popoverLeftToLeft = this.xPopoverTip
      ? this.popoverAnchor.left - this.popoverWidthPx - this.POPOVER_TIP_WIDTH_SIDE
      : this.popoverAnchor.left - this.popoverWidthPx + 39
    const popoverLeftToRight = this.popoverAnchor.left + this.popoverAnchor.width + this.POPOVER_TIP_WIDTH_SIDE
    const popoverLeft = this.popoverSidePosition === 'left' ? popoverLeftToLeft : popoverLeftToRight
    const popoverTop = this.willExceedViewportBottomEdge
      ? Math.min(
          this.realContainerHeight - this.popoverHeightPx - this.MIN_SCREEN_EDGE_PADDING,
          this.popoverAnchor.top - this.SIDE_POPOVER_TIP_DISTANCE_FROM_TOP,
        )
      : this.popoverAnchor.top +
        this.popoverAnchor.height / 2 -
        (this.xPopoverTip ? this.POPOVER_TIP_HEIGHT_SIDE / 2 : 0) -
        this.SIDE_POPOVER_TIP_DISTANCE_FROM_TOP
    const manualTopOffset = this.positionMode === 'manual' ? this.manualPosition?.boxTopOffset || 0 : 0
    return {
      top: `${popoverTop + manualTopOffset}px`,
      left: `${popoverLeft}px`,
      minHeight: '60px', // So that the tip has space to not be weird
      width: this.popoverWidth === 'auto' ? 'auto' : `${this.popoverWidthPx}px`,
    }
  }

  get popoverStyle(): Record<string, string> {
    if (this.popoverPosition === 'top') {
      return this.popoverOnTopStyle
    }
    if (this.popoverPosition === 'bottom') {
      return this.popoverToBottomStyle
    }
    return this.popoverToSideStyle
  }

  get popoverOnTopTipStyle(): Record<string, string> {
    if (this.isAnchorTooHigh) {
      return this.popoverToSideTipStyle
    }
    return {
      bottom: `${this.anchorDistanceToBottom - this.POPOVER_TIP_HEIGHT_VERTICAL}px`,
      left: `${this.popoverAnchor.left + this.popoverAnchor.width / 2 - this.POPOVER_TIP_WIDTH_VERTICAL / 2}px`,
    }
  }

  get popoverToBottomTipStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopTipStyle
    }
    return {
      top: `${this.popoverAnchor.top + this.popoverAnchor.height - this.POPOVER_TIP_HEIGHT_VERTICAL}px`,
      left: `${this.popoverAnchor.left + this.popoverAnchor.width / 2 - this.POPOVER_TIP_WIDTH_VERTICAL / 2}px`,
      transform: 'rotate(180deg)',
    }
  }

  get popoverToSideTipStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopTipStyle
    }
    if (this.popoverSidePosition === 'left') {
      return {
        top: `${this.popoverAnchor.top + this.popoverAnchor.height / 2 - this.POPOVER_TIP_HEIGHT_SIDE / 2}px`,
        left: `${this.popoverAnchor.left - this.POPOVER_TIP_WIDTH_SIDE}px`,
        transform: 'rotate(270deg)',
      }
    }
    return {
      top: `${this.popoverAnchor.top + this.popoverAnchor.height / 2 - this.POPOVER_TIP_HEIGHT_SIDE / 2}px`,
      left: `${this.popoverAnchor.left + this.popoverAnchor.width - this.POPOVER_TIP_WIDTH_SIDE}px`,
      transform: 'rotate(90deg)',
    }
  }

  get popoverTipStyle(): Record<string, string> {
    if (this.popoverPosition === 'top') {
      return this.popoverOnTopTipStyle
    }
    if (this.popoverPosition === 'bottom') {
      return this.popoverToBottomTipStyle
    }
    return this.popoverToSideTipStyle
  }

  handleWindowResize(): void {
    this.realContainerHeight = this.containerHeight || window.innerHeight
    this.realContainerWidth = this.containerWidth || window.innerWidth
    /**
     * Tells the parent component to update the anchor due to DOM changes that should have dislocate the popover
     */
    this.$emit('trigger-anchor-update')
  }

  handleWindowScroll(): void {
    /**
     * Tells the parent component to update the anchor due to DOM changes that should have dislocate the popover
     */
    this.$emit('trigger-anchor-update')
  }

  updatePopoverSize(): void {
    const popover = this.$refs.popover
    if (!popover) return
    this.popoverHeightPx = popover.$el.clientHeight
    this.autoCalculatedPopoverWidthPx = popover.$el.clientWidth
  }

  realContainerHeight = 0
  realContainerWidth = 0
  resizeObserver: ResizeObserver | null = null

  mounted(): void {
    this.$nextTick(() => {
      this.updatePopoverSize()
      if (this.positionMode === 'manual') return
      this.resizeObserver = observeSizeChange(this.$refs.popover?.$el, debounce(this.updatePopoverSize, 250))
    })
    this.realContainerHeight = this.containerHeight || window.innerHeight
    this.realContainerWidth = this.containerWidth || window.innerWidth

    window.addEventListener('resize', this.handleWindowResize)
    window.addEventListener('scroll', this.handleWindowScroll)
  }

  beforeDestroy(): void {
    this.resizeObserver?.disconnect()
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('scroll', this.handleWindowScroll)
  }

  scrimClick(): void {
    /**
     * Emitted with overlay scrim is clicked. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-click')
  }

  scrimEsc(): void {
    /**
     * Emitted when Escape key is pressed. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-esc')
  }
}

export { OzBoxColors }
